import React, { FC, useCallback, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import { ArrayHelpers, FieldArray, FormikErrors, FormikProps } from 'formik';
import { AlertContent, Button, Empty, ModalFactory, SectionHeader, TipPoolLimit } from 'components';
import { ISelectOption } from 'components/Select/types';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import useSetFirstSiteIfSingleSiteUser from 'hooks/useSetFirstSiteIfSingleSiteUser';
import { useSelectSiteEmployee } from 'modals/SelectSiteEmployee';
import { selectCurrentEmployee } from 'store/App';
import { selectCurrentEmployeeRole, selectCurrentEmployeeType } from 'store/Employers';
import { selectSiteSettings } from 'store/Site/selectors';
import { selectCurrentUser } from 'store/User';
import { IAcceptancePayout, IEmployee, IJob, IPayee } from 'types';
import { calculateHoldbackValue, centsToDollarsDisplay, checkTipAcceptancePayoutForErrors, getRolePolicy, isEmployeePayable, siteUsesHoldbacks } from 'utils';
import { PayeesContainer, PaymentsListItem } from '../components';
import { ITipAcceptanceValues } from './types';

interface IEditModeProps {
  allowHoldbacks?: boolean;
  formik: FormikProps<ITipAcceptanceValues>;
}

export const EditMode: FC<IEditModeProps> = ({ allowHoldbacks, formik }) => {
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentEmployeeType = useSelector(selectCurrentEmployeeType);
  const currentEmployeeRole = useSelector(selectCurrentEmployeeRole);
  useSetFirstSiteIfSingleSiteUser();
  const currentSiteId = currentEmployee?.siteId || '';
  const { id: currentUserId } = useSelector(selectCurrentUser());

  const { holdback: siteHoldbacks, rolePolicy, perTipMax } = useSelector(selectSiteSettings()) || {};
  const currentEmployeePolicy = getRolePolicy(rolePolicy, currentEmployeeType, currentEmployeeRole);

  const [deletePayee, setDeletePayee] = useState<() => void>(() => {});

  // Remove User Modal
  const [showRemoveModal, hideRemoveModal] = useModal(() => {
    const handleRemovePayee = () => {
      deletePayee();
      hideRemoveModal();
    };
    const handleCancelRemovePayee = () => {
      hideRemoveModal();
    };

    return (
      <ModalFactory customModal onClose={hideRemoveModal} isActive>
        <AlertContent title="Remove payee?" message="Are you sure you want to remove this payee?" leftActionLabel="Cancel" rightActionLabel="Remove" onLeftAction={handleCancelRemovePayee} onRightAction={handleRemovePayee} />
      </ModalFactory>
    );
  }, [deletePayee]);

  const isEmployeeDisabled = useCallback(
    (employee: IEmployee): [boolean, string] => {
      if (formik.values?.payouts?.some((payout) => payout.employee.id === employee.id)) {
        return [true, 'Employee has already been added'];
      }

      if (rolePolicy && !isEmployeePayable(employee, rolePolicy, currentEmployee)) {
        return [true, 'You are not allowed to issue tips to this user.'];
      }

      return [false, ''];
    },
    [formik.values?.payouts]
  );

  const onAddEmployee = useCallback(
    (employee: IEmployee) => {
      const site = employee.sites.find((site) => site.id === currentSiteId);
      const holdbackAmount = allowHoldbacks ? calculateHoldbackValue(site.disbursementMethod, siteHoldbacks, 0) : 0;

      formik.setFieldValue('payouts', [
        ...formik.values.payouts,
        {
          employee,
          amount: 0,
          payments: 0,
          holdbackAmount: holdbackAmount || 0,
          note: '',
        },
      ]);
    },
    [formik, formik.values?.payouts, siteHoldbacks]
  );

  const [showAddEmployeeModal] = useSelectSiteEmployee({ isEmployeeDisabled, onEmployeeSelect: onAddEmployee });

  const onPayoutLimitChange = useCallback(
    (value: string) => {
      formik.setFieldValue('payoutLimit', {
        ...formik.values.payoutLimit,
        value: parseInt(value, 10),
      });
    },
    [formik.values.payoutLimit]
  );

  const onPayoutLimitToggle = useCallback(() => {
    formik.setFieldValue('payoutLimit', {
      ...formik.values.payoutLimit,
      isActive: !formik.values.payoutLimit.isActive,
    });
  }, [formik.values?.payoutLimit]);

  const renderItems = (arrayHelpers: ArrayHelpers) => {
    if (formik.values?.payouts?.length === 0) {
      return <Empty container containerHeight={250} icon="People" message="No Payees" />;
    }

    return formik.values?.payouts?.map((payout, index) => {
      const site = payout.employee?.sites?.find((s) => s.id === currentSiteId);
      const payee = {
        ...payout.employee,
        disbursementMethod: site?.disbursementMethod,
        job: site?.job || { title: DEFAULT_ORG_ADMIN_JOB_TITLE },
      } as IPayee;

      const onRemovePayee = (index: number) => {
        arrayHelpers.remove(index);
      };

      return (
        <PaymentsListItem
          key={`payee_${payee.id}`}
          field="payouts"
          hasHoldback={allowHoldbacks && siteUsesHoldbacks(siteHoldbacks)}
          tipAmount={payout.amount}
          siteId={currentSiteId}
          siteHoldbacks={siteHoldbacks}
          holdback={allowHoldbacks && (payout.holdbackAmount || payout.holdbackAmount === 0) ? payout.holdbackAmount : 0}
          note={payout.notes}
          currentEmployeePolicy={currentEmployeePolicy}
          data-cy="payee-input-list-item"
          onJobChange={(jobOptions: ISelectOption<IJob>) => {
            arrayHelpers.replace(index, {
              ...payout,
              holdbackAmount: allowHoldbacks ? calculateHoldbackValue(site.disbursementMethod, siteHoldbacks, payout.amount) : 0,
              employee: {
                ...payout.employee,
                sites: payout.employee.sites.map((site) => {
                  if (site.id !== currentSiteId) return site;

                  return {
                    ...site,
                    job: jobOptions?.value,
                  };
                }),
              },
            });
          }}
          onHoldbackChange={(holdbackAmount: number) => {
            if (!allowHoldbacks) return;

            arrayHelpers.replace(index, {
              ...payout,
              holdbackAmount,
            });
          }}
          onTipAmountChange={(tipAmount: number) => {
            arrayHelpers.replace(index, {
              ...payout,
              holdbackAmount: allowHoldbacks ? calculateHoldbackValue(site.disbursementMethod, siteHoldbacks, tipAmount) : 0,
              amount: tipAmount,
            });
          }}
          onNoteChange={(note: string) => {
            arrayHelpers.replace(index, {
              ...payout,
              note,
            });
          }}
          onRemove={() => {
            setDeletePayee(() => () => {
              onRemovePayee(index);
              // Wait for `arrayHelpers` to update after remove, then check if errors for the removed field still exist.
              // If so, manually call form validation
              setTimeout(() => {
                if (formik.errors.payouts?.[index]) formik.validateForm();
              }, 1000);
            });
            showRemoveModal();
          }}
          validateUploadedTips
          payee={payee}
          index={index}
          headerError={checkTipAcceptancePayoutForErrors(payout, site, payee, rolePolicy, currentUserId, perTipMax)}
          payeeError={!!formik.touched?.payouts?.[index] && !!formik.errors?.payouts?.[index]}
          tipAmountError={(formik.errors?.payouts?.[index] as FormikErrors<IAcceptancePayout>)?.amount || (formik.errors?.payouts?.[index] as FormikErrors<IPayee>)?.holdbackAmount}
          setFieldTouched={formik.setFieldTouched}
          sitePayeeLimitMin="$1.00"
          sitePayeeLimitMax={perTipMax && centsToDollarsDisplay(perTipMax)}
          isAcceptancePayment
        />
      );
    });
  };

  return (
    <>
      <SectionHeader title="Payees" dataCy="tip-acceptance-pay-tips-edit-mode-header">
        {!!formik.values.payoutLimit && (
          <TipPoolLimit
            isActive={formik.values?.payoutLimit?.isActive}
            onToggle={onPayoutLimitToggle}
            onChange={onPayoutLimitChange}
            defaultValue={formik.values?.payoutLimit?.value}
            value={formik.values?.payoutLimit?.value}
            isStrict={formik.values?.payoutLimit?.isStrict}
          />
        )}
        <Button appearance="white" size="small" label="Add User" data-cy="tip-acceptance-add-user-button" onClick={showAddEmployeeModal} />
      </SectionHeader>
      <PayeesContainer data-cy="tip-acceptance-pay-tips-edit-mode-list">
        <FieldArray name="payouts" render={renderItems} />
      </PayeesContainer>
    </>
  );
};
